<template>
  <div class="jrd" v-if="response">
    <!-- guest header -->
    <div class="jrd-head">
      <div>
        <div class="jrd-head__name">{{ response.clientName || msg('Guest') }}</div>
        <div class="jrd-head__meta">
          <span v-if="response.propertyAddress"><feather-icon icon="HomeIcon" size="12"/>{{ response.propertyAddress }}</span>
          <span v-if="response.checkInDate"><feather-icon icon="CalendarIcon" size="12"/>
            {{ response.checkInDate }}<template v-if="response.checkOutDate"> → {{ response.checkOutDate }}</template>
          </span>
          <span v-if="response.language"><feather-icon icon="GlobeIcon" size="12"/>{{ response.language.toUpperCase() }}</span>
        </div>
      </div>
      <div class="jrd-head__right">
        <b-badge :variant="response.status === 'SUBMITTED' ? 'light-success' : 'light-warning'">
          {{ msg(response.status === 'SUBMITTED' ? 'submitted' : 'draft') }}
        </b-badge>
        <small v-if="response.submittedAt" class="d-block text-muted mt-25">
          {{ $moment(response.submittedAt, 'YYYY-MM-DD HH:mm').format('DD/MM/YY HH:mm') }}
        </small>
      </div>
    </div>

    <!-- upsells produced by this journey, itemised -->
    <div v-if="pickedProducts.length" class="jrd-upsell" :class="{'is-tocharge': !response.orderId}">
      <div class="jrd-upsell__head">
        <feather-icon :icon="response.orderId ? 'ShoppingBagIcon' : 'CreditCardIcon'" size="14"/>
        <span>{{ pickedProducts.filter(p => !p.skippedReason).length }} {{ msg('itemssold') }}</span>
        <b-badge v-if="response.orderId" variant="light-primary">{{ msg('Order') }}</b-badge>
        <b-badge v-else variant="light-warning">{{ msg('tobecharged') }}</b-badge>
      </div>

      <div v-for="product in pickedProducts" :key="product.key" class="jrd-product"
           :class="{'is-skipped': !!product.skippedReason}">
        <span class="jrd-product__name">
          {{ product.name }}
          <small v-if="(product.amount || 1) > 1"> ×{{ product.amount }}</small>
          <b-badge v-if="product.skippedReason" variant="light-danger" class="ml-25"
                   v-b-tooltip.hover :title="product.skippedReason">{{ msg('skipped') }}</b-badge>
          <b-badge v-else-if="product.featureRequestId" variant="light-success" class="ml-25">
            {{ msg('requested') }}
          </b-badge>
        </span>
        <span v-if="product.price != null" class="jrd-product__price">
          <small v-if="(product.amount || 1) > 1" class="text-muted">
            {{ product.amount }} × {{ product.price }} =
          </small>
          {{ ((product.price || 0) * (product.amount || 1)).toFixed(2) }} {{ product.currency || response.currency || '€' }}
        </span>
      </div>

      <div v-if="response.totalUpsellValue" class="jrd-product jrd-product--total">
        <span class="jrd-product__name">{{ msg('Total') }}</span>
        <span class="jrd-product__price">{{ response.totalUpsellValue }} {{ response.currency || '€' }}</span>
      </div>
      <small v-if="!response.orderId" class="jrd-upsell__hint">{{ msg('tobechargedhint') }}</small>
    </div>

    <!-- what the guest confirmed while going through the journey -->
    <div v-if="taskAnswers.length" class="jrd-tasks">
      <span v-for="task in taskAnswers" :key="task.key" class="jrd-task"
            :class="task.done ? 'is-done' : 'is-open'">
        <feather-icon :icon="task.done ? 'CheckCircleIcon' : 'ClockIcon'" size="12"/>
        {{ task.label }}
      </span>
    </div>

    <!-- answers, rendered against the stored definition snapshot -->
    <div class="jrd-answers">
      <div v-for="group in grouped" :key="group.stepTitle || 'default'" class="jrd-group">
        <div v-if="group.stepTitle" class="jrd-group__title">{{ group.stepTitle }}</div>
        <div v-for="item in group.items" :key="item.key" class="jrd-answer">
          <div class="jrd-answer__label">{{ item.label }}</div>
          <div v-if="item.products && item.products.length" class="jrd-answer__value">
            <div v-for="product in item.products" :key="product.guestFeatureId" class="jrd-answer__product">
              <span>{{ product.name || product.guestFeatureId }}<small v-if="(product.amount || 1) > 1"> ×{{ product.amount }}</small></span>
              <span v-if="product.price != null" class="tabular">
                {{ ((product.price || 0) * (product.amount || 1)).toFixed(2) }} {{ product.currency || response.currency || '€' }}
              </span>
            </div>
          </div>
          <div v-else class="jrd-answer__value" :class="{'is-empty': !item.display}">
            {{ item.display || '—' }}
          </div>
        </div>
      </div>
      <div v-if="grouped.length === 0" class="text-muted text-center py-2">{{ msg('noanswers') }}</div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'

import {VBTooltip} from 'bootstrap-vue'

/** Read-only view of one guest submission, based on the stored snapshot. */
export default {
  name: 'journeyResponseDetail',
  directives: {'b-tooltip': VBTooltip},
  props: {
    response: {type: Object, default: null},
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    answersByKey() {
      return (this.response.answers || []).reduce((map, answer) => {
        map[answer.questionKey] = answer;
        return map;
      }, {});
    },
    /** every product across all answers — incl. skipped ones, with the reason */
    pickedProducts() {
      const list = [];
      (this.response.answers || []).forEach(answer => {
        (answer.products || []).forEach((product, index) => {
          list.push({
            key: answer.questionKey + ':' + (product.guestFeatureId || index),
            name: product.name || product.guestFeatureId,
            amount: product.amount || 1,
            price: product.price,
            currency: product.currency,
            featureRequestId: product.featureRequestId,
            skippedReason: product.skippedReason,
          });
        });
      });
      return list;
    },
    /** booking tasks (payment / identity / signature / details) and their state at submit */
    taskAnswers() {
      const types = ['GUEST_DETAILS', 'IDENTITY_VERIFICATION', 'PAYMENT', 'SIGNATURE'];
      return (this.response.answers || [])
          .filter(answer => types.includes(answer.type))
          .map(answer => ({
            key: answer.questionKey,
            label: answer.label || answer.questionKey,
            done: answer.booleanValue === true,
          }));
    },
    /** definition snapshot keeps the original wording and order */
    grouped() {
      const definition = this.response.definition || [];
      const source = definition.length
          ? definition
          : (this.response.answers || []).map(answer => ({
            key: answer.questionKey, label: answer.label, stepTitle: null,
          }));

      const groups = [];
      source.forEach(item => {
        const answer = this.answersByKey[item.key];
        // skip questions the guest never saw (conditional)
        if (!answer && definition.length) return;
        const title = item.stepTitle || null;
        let group = groups.find(candidate => candidate.stepTitle === title);
        if (!group) {
          group = {stepTitle: title, items: []};
          groups.push(group);
        }
        group.items.push({
          key: item.key,
          label: item.label || item.key,
          display: this.display(answer),
          products: answer && Array.isArray(answer.products) && answer.products.length ? answer.products : null,
        });
      });
      return groups;
    },
  },
  methods: {
    display(answer) {
      if (!answer) return '';
      if (Array.isArray(answer.optionLabels) && answer.optionLabels.length)
        return answer.optionLabels.join(', ') + (answer.otherText ? ` (${answer.otherText})` : '');
      if (Array.isArray(answer.optionValues) && answer.optionValues.length)
        return answer.optionValues.join(', ');
      if (Array.isArray(answer.products) && answer.products.length)
        return answer.products.map(product => `${product.name || product.guestFeatureId} ×${product.amount || 1}`).join(', ');
      if (Array.isArray(answer.documentIds) && answer.documentIds.length)
        return answer.documentIds.length + ' ' + this.msg('document');
      if (answer.booleanValue !== null && answer.booleanValue !== undefined)
        return answer.booleanValue ? this.msg('Yes') : this.msg('No');
      if (answer.rating !== null && answer.rating !== undefined) return '★ ' + answer.rating;
      if (answer.number !== null && answer.number !== undefined) return String(answer.number);
      return answer.text || answer.date || answer.time || answer.dateTime || answer.otherText || '';
    },
  }
}
</script>

<style lang="scss" scoped>
.jrd-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(34, 41, 47, .08);

  &__name {
    font-weight: 700;
    font-size: 15px;
    color: #5e5873;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    color: #6e6b7b;
    font-size: 12.5px;
    margin-top: 3px;

    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
  }

  &__right {
    text-align: right;
  }
}

.jrd-upsell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(40, 199, 111, .1);
  color: #28c76f;
  font-size: 13px;

  &.is-tocharge {
    background: rgba(255, 159, 67, .12);
    color: #ff9f43;
  }

  &__hint {
    flex-basis: 100%;
    opacity: .85;
  }
}

.jrd-upsell__head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.jrd-product {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 3px 0 3px 22px;
  font-size: 12.5px;

  &__price {
    font-weight: 700;
    white-space: nowrap;
  }

  &--total {
    border-top: 1px dashed currentColor;
    margin-top: 2px;
    padding-top: 6px;
    font-weight: 700;
  }

  &.is-skipped {
    opacity: .65;

    .jrd-product__name {
      text-decoration: line-through;
    }
  }
}

.jrd-answer__product {
  display: flex;
  justify-content: space-between;
  gap: 10px;

  small {
    color: #6e6b7b;
  }
}

.jrd-tasks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.jrd-task {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 12px;

  &.is-done {
    background: rgba(40, 199, 111, .12);
    color: #28c76f;
  }

  &.is-open {
    background: rgba(234, 84, 85, .12);
    color: #ea5455;
  }
}

.jrd-group {
  margin-top: 12px;

  &__title {
    font-weight: 700;
    color: #7367f0;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: .03em;
    margin-bottom: 6px;
  }
}

.jrd-answer {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px dashed rgba(34, 41, 47, .08);

  &__label {
    flex: 0 0 42%;
    color: #6e6b7b;
  }

  &__value {
    flex: 1;
    font-weight: 600;
    color: #5e5873;
    word-break: break-word;

    &.is-empty {
      font-weight: 400;
      color: #b9b9c3;
    }
  }
}

.dark-layout {
  .jrd-head {
    border-bottom-color: rgba(255, 255, 255, .08);

    &__name {
      color: #d0d2d6;
    }

    &__meta {
      color: #b4b7bd;
    }
  }

  .jrd-answer {
    border-bottom-color: rgba(255, 255, 255, .07);

    &__label {
      color: #b4b7bd;
    }

    &__value {
      color: #d0d2d6;
    }
  }
}
</style>
